import React, { useId } from 'react';
import ResumeAnswer from '@/features/applications/student/programs/ResumeAnswer';
import { PRIOR_ROLES } from '@/features/applications/student/programs/laAnswers';
import { candidacyService } from '@/shared/services/candidacyService';
import { formatDate } from './reviewerStages';

const yesNo = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Not answered';
};

const timeText = (slot) =>
    [slot.startTime, slot.endTime].filter(Boolean).join(' to ');

function Fact({ label, children }) {
    return (
        <div className="row mb-1">
            <dt className="col-sm-5 fw-normal text-body-secondary">{label}</dt>
            <dd className="col-sm-7 mb-0">{children}</dd>
        </div>
    );
}

// The facts a group would show, from a fragment of conditional facts
const itemsOf = (fragment) =>
    React.Children.toArray(fragment.props.children).filter(Boolean);

function Group({ title, items }) {
    const id = useId();
    if (items.length === 0) return null;
    return (
        <div role="group" aria-labelledby={id} className="mb-3">
            <p id={id} className="small fw-semibold mb-1">
                {title}
            </p>
            <dl className="mb-0">{items}</dl>
        </div>
    );
}

/**
 * The few answers a reviewer decides on, read before the full application:
 * new or returning, the orientation and seminar answers (asked of a new LA
 * only), when the student can work, the commitments they agreed to, and the
 * resume. Shows only what the server sent this reviewer, under a "Key
 * facts" heading at `headingLevel`; with none of these answers there is no
 * heading either.
 */
export default function CandidateKeyFacts({
    profile,
    mode,
    candidacyId,
    headingLevel = 4,
}) {
    if (!profile) return null;
    const details = profile.laDetails;
    const slots = profile.availability?.schedule || [];
    const hours = profile.availability?.hoursPerWeek;
    const commitments = profile.commitments;
    const roles = (details?.priorRoles || []).map(
        (value) =>
            PRIOR_ROLES.find((role) => role.value === value)?.label || value
    );
    const fetchResume =
        mode !== 'coordinator' && candidacyId
            ? () => candidacyService.downloadResume(candidacyId)
            : undefined;

    const experience = itemsOf(
        <>
            {typeof details?.isReturning === 'boolean' && (
                <Fact label="LA experience">
                    {details.isReturning ? 'Returning LA' : 'New LA'}
                </Fact>
            )}
            {profile.major && <Fact label="Major">{profile.major}</Fact>}
            {roles.length > 0 && (
                <Fact label="Peer-support roles held">{roles.join(', ')}</Fact>
            )}
            {details?.isReturning === false && (
                <Fact label="Can attend the orientation">
                    {yesNo(details.canAttendOrientation)}
                </Fact>
            )}
            {details?.isReturning === false && (
                <Fact label="Can attend the pedagogy seminar">
                    {yesNo(details.canAttendPedagogy)}
                </Fact>
            )}
        </>
    );
    const when = itemsOf(
        <>
            {hours != null && <Fact label="Hours a week">{hours}</Fact>}
            {slots.length > 0 && (
                <Fact label="Available">
                    {slots
                        .map((slot) =>
                            [slot.day, timeText(slot)].filter(Boolean).join(' ')
                        )
                        .join('; ')}
                </Fact>
            )}
        </>
    );
    const commitmentsAndResume = itemsOf(
        <>
            {commitments && (
                <Fact label="Commitments">
                    {commitments.acknowledgedAt
                        ? `Agreed on ${formatDate(commitments.acknowledgedAt)}`
                        : commitments.acknowledged
                          ? 'Agreed'
                          : 'Not agreed yet'}
                </Fact>
            )}
            {profile.resume && (
                <Fact label="Resume">
                    <ResumeAnswer
                        application={{
                            resume: profile.resume,
                            id: profile.id,
                        }}
                        fetchResume={fetchResume}
                    />
                </Fact>
            )}
        </>
    );
    if (!experience.length && !when.length && !commitmentsAndResume.length) {
        return null;
    }
    const Heading = `h${Math.min(headingLevel, 6)}`;
    return (
        <section className="mt-3">
            <Heading className="h6">Key facts</Heading>
            <Group title="Experience" items={experience} />
            <Group title="When they can work" items={when} />
            <Group
                title="Commitments and resume"
                items={commitmentsAndResume}
            />
        </section>
    );
}
